import React, { useState, useEffect } from 'react';
import { CheckSquare, Square, Clock, ArrowRight, Plus } from 'lucide-react';
import { masterSupabase } from "@/lib/supabase-master.js";

/**
 * TodoDashboardWidget - Kompakte To-Do Übersicht für Dashboard
 * Zeigt die 3 wichtigsten Aufgaben mit Priorität und Abhak-Funktion
 */
const TodoDashboardWidget = () => {
  const [todos, setTodos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTodos();
  }, []);

  const loadTodos = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data: todosData, error } = await masterSupabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Fehler beim Laden der To-Dos:', error);
        setTodos([]);
      } else {
        setTodos(todosData || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der To-Dos:', error);
      setTodos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTodo = async (id) => {
    try {
      const todo = todos.find(t => t.id === id);
      if (!todo) return;

      const newStatus = todo.status === 'done' ? 'open' : 'done';
      
      const { error } = await masterSupabase
        .from('todos')
        .update({ status: newStatus })
        .eq('id', id);

      if (!error) {
        setTodos(todos.map(todo => 
          todo.id === id ? { ...todo, status: newStatus } : todo
        ));
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des To-Dos:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1:
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 2:
        return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20';
      case 3:
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 4:
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 5:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'Sehr hoch';
      case 2: return 'Hoch';
      case 3: return 'Normal';
      case 4: return 'Niedrig';
      case 5: return 'Sehr niedrig';
      default: return 'Normal';
    }
  };

  // Top 3 unerledigte Aufgaben nach Priorität sortiert
  const getTopTodos = () => {
    return todos
      .filter(todo => todo.status !== 'done')
      .sort((a, b) => (a.priority || 3) - (b.priority || 3)) // Niedrigere Zahl = höhere Priorität
      .slice(0, 3);
  };

  const topTodos = getTopTodos();
  const completedCount = todos.filter(todo => todo.status === 'done').length;
  const totalCount = todos.length;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded mt-0.5"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
             onClick={() => window.location.href = '/activities#todo-section'}>
          <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            To-Do Liste
          </h3>
        </div>
        <button
          onClick={() => window.location.href = '/activities#todo-section'}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          Alle anzeigen
        </button>
      </div>

      {/* Fortschrittsanzeige */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 dark:text-gray-400">Fortschritt</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {completedCount} von {totalCount} erledigt
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
          ></div>
        </div>
      </div>

      {/* Top 3 Aufgaben */}
      <div className="space-y-3">
        {topTodos.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-sm">Alle Aufgaben erledigt!</p>
            <p className="text-xs mt-1">Großartige Arbeit! 🎉</p>
          </div>
        ) : (
          topTodos.map(todo => (
            <div
              key={todo.id}
              className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <button
                onClick={() => toggleTodo(todo.id)}
                className="flex-shrink-0 mt-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {todo.status === 'done' ? (
                  <CheckSquare className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className={`text-sm font-medium ${
                    todo.status === 'done' 
                      ? 'text-gray-500 dark:text-gray-400 line-through' 
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {todo.title}
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${getPriorityColor(todo.priority)}`}>
                    {getPriorityText(todo.priority)}
                  </span>
                </div>
                
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
                    {todo.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{todo.due_date ? new Date(todo.due_date).toLocaleDateString('de-DE') : '–'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Weitere Aufgaben Counter */}
      {todos.filter(todo => todo.status !== 'done').length > 3 && (
        <div className="text-center pt-3 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => window.location.href = '/activities#todo-section'}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
          >
            {todos.filter(todo => todo.status !== 'done').length - 3} weitere Aufgaben anzeigen
          </button>
        </div>
      )}
    </div>
  );
};

export default TodoDashboardWidget;

